import { useState } from 'react';
import {
    IComponentCss,
    VerticalTabsCustom,
    TitlePageH1,
} from '@tap/mui-ui';
import { Grid2, Typography, Box } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { TabComponent, TabLabel, TabsTitle } from '../ReportParameterElements';
import { docAnalyticsService, RecentlyCreatedReportRow } from '@tap/data-providers';
import PeriodSelect, { PeriodSelectType, PeriodSelectTypeSpec } from '@tap/mui-ui/src/Form/PeriodSelect';
import { RecentlyCreatedIssuesReport } from './RecentlyCreatedIssuesReport';
import { LoadingButton } from '@mui/lab';
import { SelectDepartmentVerticalTab, useDepartment } from '@tap/tap-ui';
import { NotDataForReportPage } from '../../not-data-for-report/NotDataForReportPage';
import { dateTimeHelper } from '@tap/utils';

/*
*   Отчет Недавно созданные заявки
*/

enum CurrentWindow {
    Options = 'options',
    Chart = 'chart',
    NotData = 'not-data',
}

const css: IComponentCss = {
    titleButton: {
        marginTop: '10px',
        minWidth: '189px',
        maxHeight: '39.5px',
    },
    title: {
        width: '100%',
        marginBottom: '16px',
    },
};

type RecentlyCreatedIssuesPageFormProps = {
    periodType: PeriodSelectType,
    departmentIds: number[],
}

export const RecentlyCreatedIssuesPage = () => {
    const [currentWindow, setCurrentWindow] = useState<CurrentWindow>(CurrentWindow.Options);
    const departmentProps = useDepartment();
    const [reportData, setReportData] = useState<RecentlyCreatedReportRow[]>([]);
    const todayStart = new Date(new Date().setHours(0, 0, 0, 0));

    // Запрос расчета
    const { values, setFieldValue, ...formik } = useFormik<RecentlyCreatedIssuesPageFormProps>({
        validateOnChange: true,
        validateOnBlur: false,
        initialValues: {
            periodType: PeriodSelectType.LastWeek,
            departmentIds: [],
        },
        validationSchema: Yup.object({
            periodType: Yup.string().required(),
        }),
        onSubmit: async (data) => {
            const isAllDepartmentsSelected = data.departmentIds.length === 0 || data.departmentIds.length === departmentProps.sectorList.length;
            const selectedManufactories = departmentProps.manufactoryList.filter(m => m.sectors.filter(s => data.departmentIds.includes(s.id)).length).map(m => m.name);
            const params = {
                manufactories: selectedManufactories,
                departmentIds: isAllDepartmentsSelected ? [] : data.departmentIds,
                endToUtc: new Date(),
                startFromUtc: dateTimeHelper.getPreviousDate(todayStart, PeriodSelectTypeSpec[data.periodType].daysQuantity),
            }

            await docAnalyticsService.getIssueRecentlyCreatedReport(params)
                .then((res) => {
                    if (!res.length) {
                        setCurrentWindow(CurrentWindow.NotData);
                        return;
                    }
                    setReportData(res);
                    setCurrentWindow(CurrentWindow.Chart);
                })

        },
    });

    const handleSelectedPeriodChange = (periodType: PeriodSelectType) => {
        setFieldValue('periodType', periodType);
    };

    return (
        <>

            {currentWindow === CurrentWindow.Options &&
                <>
                    <Grid2
                        container
                        mb={4}
                        justifyContent='space-between'
                        sx={{ marginBottom: 0 }}
                    >
                        <TitlePageH1>
                            Недавно созданные заявки
                        </TitlePageH1>
                        <LoadingButton
                            variant='contained'
                            color='primary'
                            loading={formik.isSubmitting}
                            disabled={formik.isSubmitting || !formik.isValid}
                            sx={css.titleButton}
                            onClick={formik.submitForm}
                        >
                            Запрос отчёта
                        </LoadingButton>
                    </Grid2>
                    <VerticalTabsCustom
                        isCacheTabs
                        defaultTab={1}
                        height={'calc(100vh - 210px)'}
                        tabsArray={[
                            {   // Выберите параметры - заголовок табов
                                tabButton: {
                                    label:
                                        <TabsTitle
                                            title='Параметры'
                                        />,
                                    otherProps: {
                                        wrapped: true,
                                        disabled: true,
                                        sx: { padding: 0 },
                                    },
                                },
                                tabComponent: <></>,
                            },
                            {   // Общие
                                tabButton: {
                                    label:
                                        <TabLabel
                                            title={'Общие'}
                                            subTitle={
                                                `Период: ${PeriodSelectTypeSpec[values.periodType]?.name.toLowerCase()}`
                                            }
                                        />,
                                    otherProps: {
                                        wrapped: true,
                                        sx: { padding: 0 },
                                    },
                                },
                                tabComponent:
                                    <TabComponent title='Общие'>
                                        <Box width={'100%'}>
                                            <Typography sx={css.title}>
                                                Период
                                            </Typography>
                                            <PeriodSelect
                                                selectedPeriodType={values.periodType}
                                                handleSelectedPeriodChange={(periodType) => handleSelectedPeriodChange(periodType)}
                                            />
                                        </Box>
                                    </TabComponent>,
                            },
                            {   // Цех и участок
                                tabButton: {
                                    label: <SelectDepartmentVerticalTab.TabLabel {...departmentProps}
                                        selectedDepartmentIds={values.departmentIds}
                                    />,
                                    otherProps: {
                                        wrapped: true,
                                        sx: { padding: 0 },
                                    },
                                },
                                tabComponent: (
                                    <SelectDepartmentVerticalTab.TabContent {...departmentProps}
                                        selectedDepartmentIds={values.departmentIds}
                                        onChange={(departmentIds) => setFieldValue('departmentIds', departmentIds)}
                                    />
                                ),
                            },
                        ]}
                    />
                </>
            }

            {/* График */}
            {currentWindow === CurrentWindow.Chart &&
                <RecentlyCreatedIssuesReport
                    reportData={reportData}
                    periodType={values.periodType}
                    onOptionsButtonClick={() => setCurrentWindow(CurrentWindow.Options)} />
            }

            {/* Заглушка на случай, если нет данных для формирования отчета. */}
            {currentWindow === CurrentWindow.NotData &&
                <NotDataForReportPage onReturnButtonClick={() => setCurrentWindow(CurrentWindow.Options)} />
            }
        </>
    );
}
